import styled from '@emotion/styled';
import ServicePageTemplate from 'presentations/views/components/templates/ServicePageTemplate';
import { background, gray03 } from 'presentations/views/styles/color';
import Header from 'presentations/views/components/organisms/Header';
import TopMainFeature from 'presentations/views/components/organisms/service/TopMainFeature';
import CompanyList from 'presentations/views/components/organisms/service/ServiceCompanyList';
import UseCaseList from 'presentations/views/components/organisms/service/ServiceUseCaseList';
import ReviewList from 'presentations/views/components/organisms/service/ServiceReviewList';
import IntegrationList from 'presentations/views/components/organisms/service/ServiceIntegrationList';
import TopScheduling from 'presentations/views/components/organisms/service/TopScheduling';
import TopCalendar from 'presentations/views/components/organisms/service/TopCalendar';
import TopBooking from 'presentations/views/components/organisms/service/TopBooking';
import TopDesign from 'presentations/views/components/organisms/service/TopDesign';
import ImageSectionContainer from 'presentations/views/components/organisms/service/ImageSectionContainer';
import TopForm from 'presentations/views/components/organisms/service/TopForm';
import Certification from 'presentations/views/components/pages/products/SecurityPage/Certification';
import TopMeeting from 'presentations/views/components/organisms/service/TopMeeting';
import OddSectionContainer from 'presentations/views/components/organisms/service/OddSectionContainer';
import { ViewerItem } from 'presentations/api/schema/user';
import { isMediaMax, mediaMax, spacing } from 'presentations/views/styles';
import { useReferralTrack } from 'presentations/views/hooks/track';
import {
  ChangelogListItem,
  CustomerStoryListItem,
} from 'presentations/api/schema/contents';
import TopAssistant from '../../organisms/service/TopAssistant';
import { apiPath, productSecurityPath } from 'presentations/routes/path';
import TopMainVisual from 'presentations/views/components/organisms/service/TopMainVisual';
import { setEvent } from 'presentations/views/helpers/analytics';
import BackgroundContainer from '../../organisms/service/BackgroundContainer';
import { useRequest } from 'presentations/views/hooks/request';
import { useRouter } from 'next/router';
import { imagePath } from 'presentations/assets';
import { useEffect, useState } from 'react';
import { useThrottle } from '../../atoms/Calendar/hooks/useThrottle';

interface Props {
  viewer: ViewerItem | null;
}

export default function Component(props: Props) {
  useReferralTrack();
  const [isHeaderDark, setIsHeaderDark] = useState<boolean>(true);
  const isMobile = isMediaMax('tablet');

  const router = useRouter();
  const changelogRequest = useRequest(
    apiPath(`/v1/contents/changelog`, {
      page: 1,
      perPage: 1,
      locale: router.locale,
    }),
  );
  const changelogs: ChangelogListItem[] = changelogRequest.data || [];
  const customersRequest = useRequest(
    apiPath('/v1/contents/customers', {
      perPage: 3,
      locale: router.locale,
    }),
  );
  const customers: CustomerStoryListItem[] = customersRequest.data || [];

  const onScroll = useThrottle((e: any) => {
    const changeY = isMobile ? 400 : 637;
    setIsHeaderDark(window.scrollY < changeY);
  }, 200);

  useEffect(() => {
    window.addEventListener('scroll', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [isMobile]);

  const onClickInquiry = () => {
    setEvent('click corporative inquiry', {
      source: 'jicoo',
      product: 'top',
    });
  };

  return (
    <ServicePageTemplate
      isVisibleSupport={true}
      header={
        <TopHeader
          viewer={props.viewer}
          // isTransparent={!isMobile}
          isTransparent={isHeaderDark}
          isDarkable={isHeaderDark}
          backgroundNode={<HeaderBackground isTransparent={!isHeaderDark} />}
        />
      }
      breadcrumbs={[]}
    >
      <MainVisualContainer>
        <TopMainVisual
          changelog={changelogs[0] || null}
          onClickInquiry={onClickInquiry}
          isShowSignUp
          size="large"
          viewer={props.viewer}
        />
      </MainVisualContainer>
      <TopFeatureContainer>
        <TopMainFeature />
      </TopFeatureContainer>
      <CompanyContainer>
        <CompanyListWrap>
          <CompanyList />
        </CompanyListWrap>
      </CompanyContainer>
      <ImageItemSectionContainer>
        <OddSectionContainer>
          <ImageSectionContainer>
            <TopBooking />
          </ImageSectionContainer>
        </OddSectionContainer>
        <ImageSectionContainer>
          <TopScheduling />
        </ImageSectionContainer>
        <OddSectionContainer>
          <ImageSectionContainer>
            <TopCalendar variant="slide" />
          </ImageSectionContainer>
        </OddSectionContainer>
        <ImageSectionContainer>
          <TopDesign />
        </ImageSectionContainer>
        <OddSectionContainer>
          <ImageSectionContainer>
            <TopAssistant variant="slide" />
          </ImageSectionContainer>
        </OddSectionContainer>
        <ImageSectionContainer>
          <TopForm variant="slide" />
        </ImageSectionContainer>
        <OddSectionContainer>
          <ImageSectionContainer>
            <TopMeeting />
          </ImageSectionContainer>
        </OddSectionContainer>
      </ImageItemSectionContainer>
      <SectionContainer>
        <Certification link={productSecurityPath()} />
      </SectionContainer>
      <SectionContainer>
        <IntegrationList />
      </SectionContainer>
      <SectionContainer>
        <UseCaseList customers={customers} />
      </SectionContainer>
      <SectionContainer>
        <ReviewList />
      </SectionContainer>
    </ServicePageTemplate>
  );
}

const SectionContainer = styled.div`
  max-width: 1080px;
  margin: 0 auto;
  padding: ${spacing(128)} ${spacing(16)} ${spacing(96)};
  ${mediaMax.tablet} {
    padding: ${spacing(48)} ${spacing(16)};
  }
  border-bottom: 1px solid ${gray03};
  :last-of-type {
    /* border-bottom: 0; */
  }
`;
const ImageItemSectionContainer = styled.div`
  padding: ${spacing(64)} 0 0;
  ${mediaMax.tablet} {
    padding: ${spacing(48)} 0 0;
  }
  border-bottom: 1px solid ${gray03};
  :last-of-type {
    /* border-bottom: 0; */
  }
`;
const CompanyContainer = styled.div`
  position: relative;
  padding: 0 ${spacing(24)};
  ${mediaMax.tablet} {
    margin-top: ${spacing(32)};
    width: 100%;
    padding: 0;
  }
`;
const CompanyListWrap = styled.div`
  position: relative;
  z-index: 1;
`;
const MainVisualContainer = styled.div`
  margin-top: -80px;
  background: url(${imagePath('pages/top/202402/bg-grid.png', {
      width: 1283,
      height: 637,
      dpr: 2,
    })})
    no-repeat center top;
  background-size: 1283px auto;
  background-color: ${background};
  ${mediaMax.tablet} {
    margin-top: -54px;
  }
`;

const HeaderBackground = styled.div<{ isTransparent: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #121212bb;
  backdrop-filter: blur(2px);
  opacity: ${(props) => (props.isTransparent ? 0 : 1)};
  transition: all 300ms ease-out;
  z-index: -1;
  ${mediaMax.tablet} {
    /* display: none; */
  }
`;
const TopFeatureContainer = styled(BackgroundContainer)`
  text-align: center;
  padding: ${spacing(64)} 0 ${spacing(32)};
  z-index: 0;

  ${mediaMax.tablet} {
    padding: 40px 40px 80px;
  }

  ${mediaMax.tablet} {
    padding: 24px 0 80px;
  }

  ${mediaMax.smartphone} {
    padding: 40px 0 40px;
  }
`;

const TopHeader = styled(Header)`
  border-bottom: 0;
`;
